import { useEffect, useState } from 'react';
import { cn } from '~/lib/utils';
import styles from './NotificationsChaos.module.css';

type Side = 'left' | 'right';

interface NotificationConfig {
  key: string;
  src: string;
  side: Side;
  topPct: number;
  edgePct: number;
  widthCqw: number;
  rotateDeg: number;
  appearAtMs: number;
}

/**
 * appearAtMs is an accelerando: the gap between cards shrinks from 600ms to 90ms, so the pile starts
 * as the odd ping and ends as a flood. The old timings clustered in threes with ~600ms of dead air
 * between clusters, which read as stalling rather than building.
 */
const NOTIFICATIONS: NotificationConfig[] = [
  { key: 'slack',           src: '/landing/problem-section/notifications/slack-notification.svg',            side: 'right', topPct:  4,  edgePct: 1.5, widthCqw: 31, rotateDeg: -1.6, appearAtMs:    0 },
  { key: 'notes',           src: '/landing/problem-section/notifications/notes.svg',                         side: 'left',  topPct:  6,  edgePct: 1.5, widthCqw: 31, rotateDeg:  1.2, appearAtMs:  600 },
  { key: 'gmail',           src: '/landing/problem-section/notifications/gmail-notification.svg',            side: 'right', topPct: 16,  edgePct: 3.0, widthCqw: 31, rotateDeg:  1.8, appearAtMs: 1120 },
  { key: 'figma-screens',   src: '/landing/problem-section/notifications/figma-screens.svg',                 side: 'left',  topPct: 21,  edgePct: 1.0, widthCqw: 31, rotateDeg: -2.4, appearAtMs: 1570 },
  { key: 'calendar',        src: '/landing/problem-section/notifications/calendar-notification.svg',         side: 'right', topPct: 28,  edgePct: 1.0, widthCqw: 31, rotateDeg: -2.6, appearAtMs: 1960 },
  { key: 'sheets',          src: '/landing/problem-section/notifications/sheets-notification.svg',           side: 'left',  topPct: 39,  edgePct: 2.5, widthCqw: 31, rotateDeg:  2.0, appearAtMs: 2290 },
  { key: 'linear',          src: '/landing/problem-section/notifications/linear-notification.svg',           side: 'right', topPct: 40,  edgePct: 3.0, widthCqw: 31, rotateDeg:  1.2, appearAtMs: 2570 },
  { key: 'notion',          src: '/landing/problem-section/notifications/notion-notification.svg',           side: 'left',  topPct: 58,  edgePct: 1.5, widthCqw: 31, rotateDeg: -1.5, appearAtMs: 2800 },
  { key: 'messages',        src: '/landing/problem-section/notifications/messages-notification.svg',         side: 'right', topPct: 52,  edgePct: 1.5, widthCqw: 31, rotateDeg: -1.0, appearAtMs: 2990 },
  { key: 'figma',           src: '/landing/problem-section/notifications/figma-notification.svg',            side: 'left',  topPct: 73,  edgePct: 3.0, widthCqw: 31, rotateDeg:  2.4, appearAtMs: 3140 },
  { key: 'reminder',        src: '/landing/problem-section/notifications/reminder-notification.svg',         side: 'right', topPct: 64,  edgePct: 2.5, widthCqw: 31, rotateDeg:  2.2, appearAtMs: 3260 },
  { key: 'google-meeting',  src: '/landing/problem-section/notifications/google-meeting-notification.svg',   side: 'right', topPct: 78,  edgePct: 3.0, widthCqw: 31, rotateDeg: -1.4, appearAtMs: 3350 },
];

/** Cards enter from, and leave through, the edge they sit against. Percent of the card's own width. */
const ENTER_TRAVEL_PCT = 10;

/** Newest card clears first, so the pile unwinds instead of blinking out in one frame. */
const EXIT_STAGGER_MS = 26;

const NOTIFICATIONS_LAST_APPEAR_MS = NOTIFICATIONS.reduce(
  (m, n) => Math.max(m, n.appearAtMs),
  0,
);

export interface NotificationsChaosProps {
  className?: string;
  cycleKey?: number;
  dissolving?: boolean;
  startDelayMs?: number;
}

export const NotificationsChaos = ({
  className,
  cycleKey = 0,
  dissolving = false,
  startDelayMs = 1100,
}: NotificationsChaosProps) => {
  const [visibleSet, setVisibleSet] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setVisibleSet(new Set());

    const timers: ReturnType<typeof setTimeout>[] = [];

    NOTIFICATIONS.forEach((notif) => {
      timers.push(
        setTimeout(() => {
          setVisibleSet((prev) => {
            const next = new Set(prev);
            next.add(notif.key);
            return next;
          });
        }, startDelayMs + notif.appearAtMs),
      );
    });

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [cycleKey, startDelayMs]);

  return (
    <div className={cn(styles.notificationsChaos, className)} aria-hidden="true">
      {NOTIFICATIONS.map((notif, i) => {
        const isVisible = !dissolving && visibleSet.has(notif.key);
        const horizontalRule =
          notif.side === 'left'
            ? { left: `${notif.edgePct}%` }
            : { right: `${notif.edgePct}%` };

        const enterX =
          notif.side === 'left' ? `-${ENTER_TRAVEL_PCT}%` : `${ENTER_TRAVEL_PCT}%`;

        return (
          <div
            key={notif.key}
            className={cn(
              styles.notification,
              isVisible && styles.visible,
              dissolving && styles.fading,
            )}
            style={{
              top: `${notif.topPct}%`,
              ...horizontalRule,
              width: `${notif.widthCqw}cqw`,
              ['--notif-rotate' as string]: `${notif.rotateDeg}deg`,
              ['--notif-enter-x' as string]: enterX,
              transitionDelay: dissolving
                ? `${(NOTIFICATIONS.length - 1 - i) * EXIT_STAGGER_MS}ms`
                : undefined,
              zIndex: 3 + i,
            }}
          >
            <img src={notif.src} alt="" className={styles.notificationImage} />
          </div>
        );
      })}
    </div>
  );
};

NotificationsChaos.displayName = 'NotificationsChaos';
